import React, { useEffect, useState } from "react";
import {
  Mail,
  User,
  Phone,
  Briefcase,
  MapPin,
  Globe,
  Building2,
  ChevronDown,
  HousePlus,
  SquareArrowOutUpRight
} from "lucide-react";

import { useDispatch, useSelector } from "react-redux";
import { addTab } from "../../../../../../redux/slices/manageTeamSlice";
import {
  viewUserProfile,
  viewOrganizationById
} from "../../../../../../features/ACS-Dashboard-Modules/Manage-Team/api";
import { selectModuleTabs } from "../../../../../../redux/selectors/manageTeamTabSelector";
import Loader from "../../../../ACS-Blocks/Loader";

export default function ProfileInfoTab({ user, onClosePanel, onViewOrganization}) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const email = user?.email;

  // ----------------------------------------
  // Build officeCode → { orgId, officeName }
  // ----------------------------------------
  const organizationMap = (() => {
    try {
      const stored = JSON.parse(localStorage.getItem("organizationData"));
      if (!Array.isArray(stored)) return {};

      const map = {};
      stored.forEach((o) => {
        map[o.officeCode] = {
          orgId: o.orgId,
          officeName: o.officeName,
        };
      });
      return map;
    } catch {
      return {};
    }
  })();

  // ----------------------------------------
  // OPEN ORGANIZATION TAB
  // ----------------------------------------
 const handleViewOrganization = async (org) => {
  try {
    const orgId = org?.orgId || org?.id;

    if (!orgId) return;

    // 🔥 CLOSE PROFILE DROPDOWN
    onClosePanel?.();

    const res = await viewOrganizationById(orgId);
    const orgData = res.data;

    dispatch(
      addTab({
        id: `view-partner-${orgId}`,
        name: `Organization ${org.organizationName || org.officeName}`,
        contentType: "viewPreferredPartnerDetails",
        data: orgData,
        isDynamic: true,
        createdAt: Date.now(),
      })
    );
  } catch (err) {
    console.error("Failed to open organization:", err);
  }
};



  // ----------------------------------------
  // FETCH PROFILE
  // ----------------------------------------
  useEffect(() => {
    const fetchProfile = async () => {
      if (!email) return;
      try {
        const res = await viewUserProfile(email);
        setProfile(res.data);
      } catch (err) {
        console.error("Failed to load profile info", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [email]);

  if (loading) {
    return (
      <div className="p-fluid-3 text-gray-500 dark:text-acs-dark-subtext">
        <Loader
          color="text-blue-600 dark:text-acs-dark-text"
          size="h-5 w-5"
          message="Loading User Profile"
        />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-fluid-3 text-gray-500 dark:text-acs-dark-subtext">
        No profile info available.
      </div>
    );
  }

  // ----------------------------------------
  // Reusable ROW Component
  // ----------------------------------------
  const Row = ({ icon, label, value }) => (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-fluid-2">
        {icon}
        <p className="text-fluid-base text-gray-700 dark:text-acs-dark-text">
          <span className="font-medium text-gray-600 dark:text-acs-dark-subtext">
            {label}:
          </span>{" "}
          {value || "—"}
        </p>
      </div>
    </div>
  );

  return (
    <div className="space-y-fluid-3 p-fluid-1">
      {/* USER BASIC INFO */}
      <Row
        icon={<User className="w-4 h-4 text-gray-400 dark:text-gray-500" />}
        label="Full Name"
        value={`${profile.firstName || ""} ${profile.lastName || ""}`.trim()}
      />

      <Row
        icon={<Mail className="w-4 h-4 text-gray-400 dark:text-gray-500" />}
        label="Email"
        value={profile.email}
      />

      <Row
        icon={<Briefcase className="w-4 h-4 text-gray-400 dark:text-gray-500" />}
        label="Job Title"
        value={profile.job}
      />

      <Row
        icon={<Phone className="w-4 h-4 text-gray-400 dark:text-gray-500" />}
        label="Phone Number"
        value={profile.phoneNumber}
      />

      <Row
        icon={<MapPin className="w-4 h-4 text-gray-400 dark:text-gray-500" />}
        label="Address"
        value={profile.address}
      />

      <Row
        icon={<Globe className="w-4 h-4 text-gray-400 dark:text-gray-500" />}
        label="Country"
        value={profile.country}
      />

      {/* Divider */}
      <div className="border-b border-gray-300 dark:border-acs-dark-border my-fluid-2" />

      {/* ORGANIZATIONS LIST */}
      <div className="pt-fluid-1">
        <details className="group">
          <summary
            className="flex items-center justify-between cursor-pointer select-none 
            py-fluid-1 px-0 list-none"
          >
            <div className="flex items-center gap-fluid-2">
              <HousePlus className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              <p className="text-fluid-base text-gray-700 dark:text-acs-dark-text font-medium">
                Organizations Assigned
              </p>
            </div>

            <ChevronDown
              className="w-5 h-5 text-gray-500 dark:text-gray-300 transition-transform duration-300 
              group-open:rotate-180"
            />
          </summary>

          <div className="mt-fluid-3 space-y-fluid-2">
            {profile.organizations?.length ? (
              profile.organizations.map((org, idx) => {
                const mapped = organizationMap[org.officeCode];

                return (
                  <div key={org.officeCode} className="flex flex-col gap-fluid-1">
                    <div className="flex items-center justify-between">

                      {/* LEFT SIDE */}
                      <div className="flex items-center gap-fluid-2">
                        <Building2 className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <div>
                          <p className="text-fluid-base text-gray-800 dark:text-acs-dark-text">
                            {org.organizationName}
                          </p>
                          <p className="text-xs text-gray-500">{org.officeCode}</p>
                        </div>
                      </div>

                      {/* VIEW ORG BUTTON */}
                    {/* VIEW ORG BUTTON */}
                      {mapped?.orgId && (
                        <button
                          className="
                            text-[#223688] dark:text-[#31C4AB] hover:underline
                            text-xs font-medium flex items-center gap-1
                          "
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewOrganization?.(mapped);
                          }}
                        >
                          <SquareArrowOutUpRight className="w-[14px] h-[14px]" />
                        </button>
                      )}
                    </div>

                    {idx < profile.organizations.length - 1 && (
                      <div className="border-b border-gray-200 dark:border-acs-dark-border mt-fluid-1" />
                    )}
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-500 dark:text-acs-dark-subtext">
                No organizations assigned.
              </p>
            )}
          </div>
        </details>
      </div>
    </div>
  );
}
